import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Residue, TreatmentType } from "@/types/residue/residue.types";
import { InformationSource } from "@/types/information-source/information-source.types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trash2, Plus } from "lucide-react";
import { residueService } from "@/services/residue/residueService";
import { useData } from "@/hooks/useData";

interface ResidueFormProps {
  isOpen: boolean;
  onClose: () => void;
  residueToEdit?: Residue | null;
  onSuccess: () => void;
}

const TREATMENT_TYPE_OPTIONS = [
  { id: "containment", label: "Containment" },
  { id: "valorization", label: "Valorization" },
  { id: "energyValorization", label: "Energy Valorization" },
  { id: "landfill", label: "Landfill" },
];

export const ResidueForm = ({
  isOpen,
  onClose,
  residueToEdit,
  onSuccess,
}: ResidueFormProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [treatmentTypes, setTreatmentTypes] = useState<TreatmentType[]>([]);

  // Validation state
  const [nameError, setNameError] = useState("");
  const [treatmentTypesError, setTreatmentTypesError] = useState("");

  // Fetch information sources for the dropdown
  const { data: infoSources, loading: loadingInfoSources } =
    useData<InformationSource>({
      collectionName: "setup_infoSources",
    });

  // Initialize form when editing
  useEffect(() => {
    if (residueToEdit) {
      setName(residueToEdit.name || "");
      setTreatmentTypes(residueToEdit.treatment_types || []);
    } else {
      // Reset form for new creation
      setName("");
      setTreatmentTypes([]);
    }
    // Reset validation errors when opening/closing the form
    setNameError("");
    setTreatmentTypesError("");
  }, [residueToEdit, isOpen]);

  const validateForm = () => {
    let isValid = true;

    // Validate name
    if (!name.trim()) {
      setNameError(t("validation.required"));
      isValid = false;
    } else {
      setNameError("");
    }

    // Validate treatment types
    if (treatmentTypes.length === 0) {
      setTreatmentTypesError(t("residues.validation.treatmentTypesRequired"));
      isValid = false;
    } else {
      // Check if all treatment types have valid data
      const hasInvalidTreatment = treatmentTypes.some(
        (treatment) =>
          !treatment.id ||
          treatment.fe_r === undefined ||
          treatment.fe_r < 0 ||
          !treatment.infoSource
      );

      if (hasInvalidTreatment) {
        setTreatmentTypesError(t("residues.validation.invalidTreatmentData"));
        isValid = false;
      } else {
        setTreatmentTypesError("");
      }
    }

    return isValid;
  };

  const addTreatmentType = () => {
    const newTreatment: TreatmentType = {
      id: "",
      fe_r: 0,
      infoSource: "",
    };
    setTreatmentTypes([...treatmentTypes, newTreatment]);
  };

  const removeTreatmentType = (index: number) => {
    setTreatmentTypes(treatmentTypes.filter((_, i) => i !== index));
  };

  const updateTreatmentType = (
    index: number,
    field: keyof TreatmentType,
    value: any
  ) => {
    const updatedTreatments = [...treatmentTypes];
    updatedTreatments[index] = {
      ...updatedTreatments[index],
      [field]: value,
    };
    setTreatmentTypes(updatedTreatments);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const residueData: Partial<Residue> = {
        name: name.trim(),
        treatment_types: treatmentTypes,
      };

      if (residueToEdit) {
        // Update existing residue
        await residueService.edit(residueToEdit.id, residueData, user?.uid);
        toast({
          title: t("success"),
          description: t("residues.updated"),
        });
      } else {
        // Create new residue
        await residueService.create(
          residueData as Omit<Residue, "id" | "createdAt" | "updatedAt">,
          user?.uid
        );
        toast({
          title: t("success"),
          description: t("residues.created"),
        });
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving residue:", error);
      toast({
        title: t("error"),
        description: residueToEdit
          ? t("residues.updateError")
          : t("residues.createError"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter out archived info sources
  const activeInfoSources =
    infoSources?.filter((source) => !source.isArchived) || [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {residueToEdit ? t("residues.editTitle") : t("residues.addTitle")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name" className="flex items-center gap-1">
                {t("residues.name")}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={nameError ? "border-red-500" : ""}
                disabled={isLoading}
                placeholder={t("residues.namePlaceholder")}
              />
              {nameError && <p className="text-red-500 text-sm">{nameError}</p>}
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1">
                  {t("residues.treatmentTypes")}
                  <span className="text-red-500">*</span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTreatmentType}
                  disabled={isLoading}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("residues.addTreatmentType")}
                </Button>
              </div>

              {treatmentTypes.map((treatment, index) => (
                <Card key={index} className="p-4">
                  <CardHeader className="p-0 pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">
                        {t("residues.treatmentType")} {index + 1}
                      </CardTitle>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTreatmentType(index)}
                        disabled={isLoading}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 space-y-3">
                    <div className="grid gap-2">
                      <Label htmlFor={`treatment-type-${index}`}>
                        {t("residues.treatmentTypeId")}
                      </Label>
                      <Select
                        value={treatment.id}
                        onValueChange={(value) =>
                          updateTreatmentType(index, "id", value)
                        }
                        disabled={isLoading}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t("residues.selectTreatmentType")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {TREATMENT_TYPE_OPTIONS.map((option) => (
                            <SelectItem key={option.id} value={option.id}>
                              {t(`residues.treatments.${option.id}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor={`fe-r-${index}`}>
                        {t("residues.emissionFactor")}
                      </Label>
                      <Input
                        id={`fe-r-${index}`}
                        type="number"
                        step="any"
                        min="0"
                        value={treatment.fe_r}
                        onChange={(e) =>
                          updateTreatmentType(
                            index,
                            "fe_r",
                            e.target.value
                          )
                        }
                        disabled={isLoading}
                        placeholder={t("residues.emissionFactorPlaceholder")}
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor={`info-source-${index}`}>
                        {t("residues.infoSource")}
                      </Label>
                      <Select
                        value={treatment.infoSource}
                        onValueChange={(value) =>
                          updateTreatmentType(index, "infoSource", value)
                        }
                        disabled={isLoading || loadingInfoSources}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t("residues.selectInfoSource")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {activeInfoSources.map((source) => (
                            <SelectItem key={source.id} value={source.id}>
                              {source.name} ({source.year})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {treatmentTypesError && (
                <p className="text-red-500 text-sm">{treatmentTypesError}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-gray-300 hover:bg-gray-100"
              disabled={isLoading}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {isLoading ? t("saving") : t("save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
